import React, { useContext, useState, useEffect, forwardRef, useImperativeHandle, useCallback, memo } from 'react';
import { ContentContext } from '../../context/ContentContext';
import { gtagEnvelope } from '../../util/gtagEnvelope';
import { InputMapProps, InputMapRef } from '../../types';
import { TextArea } from '../TextArea';
import { Select } from '../Select';
import { Input } from '../Input';
import { RadioFieldGroup } from '../RadioFieldGroup';
import { HiddenField } from '../HiddenField';
import { ConditionalStateSelect } from '~/common/components/ConditionalStateSelect';
import { businessEmailCheck } from '~/common/util/businessEmail';
import { orderedStateList } from '~/common/util/states';
import classNames from 'classnames';
import {
  validateString,
  validateEmail,
  validatePhone,
  validateSelect,
  validateFullName,
  validateRadio,
} from '../../util/customValidation';
import { FastField, getIn } from 'formik';
import style from './inputMap.module.css';
import { FORM_TYPES } from '~/common/consts';
import { useAppStore } from '~/common/store/appStore';
import { useLatestAbortSignal } from '~/common/hooks/useLatestAbortSignal';
import { useShallow } from 'zustand/react/shallow';

export const InputMap = forwardRef<InputMapRef, InputMapProps>((props, ref) => {
  const { formikTouched, formikErrors, formikValues, setTextareaExistsSetState, formikRef } = props;
  const { formType } = useAppStore(useShallow((state) => ({ formType: state.formType })));
  const [notBusinessEmail, setNotBusinessEmail] = useState(false);
  const [checkingEmailType, setCheckingEmailType] = useState(false);
  const nextEmailTypeCheckSignal = useLatestAbortSignal();

  useImperativeHandle(
    ref,
    () => ({
      isCheckingEmailOrNotBusinessEmail: () => checkingEmailType || notBusinessEmail,
    }),
    [checkingEmailType, notBusinessEmail]
  );

  const { inputs } = useContext(ContentContext);

  const trackBlur = useCallback((id: string, type?: string) => {
    const fieldId = id || type;
    gtagEnvelope('event', 'form_field_focus', {
      form_field: `form-field_${fieldId}`,
      form_field_url: `${window.location.pathname}`,
    });
  }, []);

  const validateBusinessEmail = useCallback(async (email: string) => {
    const signal = nextEmailTypeCheckSignal();
    formikRef.current?.setFieldTouched('email', true);
    const invalid = validateEmail(email);
    if (invalid) {
      formikRef.current?.setFieldError('email', true);
      setNotBusinessEmail(false);
      setCheckingEmailType(false);
      return true;
    } else {
      setCheckingEmailType(true);
      const business = await businessEmailCheck(email, signal);
      if (signal.aborted) return false;
      if (!business) {
        formikRef.current?.setFieldError('email', true);
        setNotBusinessEmail(true);
        setCheckingEmailType(false);
        return true;
      } else {
        formikRef.current?.setFieldError('email', false);
        setNotBusinessEmail(false);
        setCheckingEmailType(false);
        validateEmail(email);
      }
    }
    return false;
  }, []);

  const isPartnerForm = formType === FORM_TYPES.PARTNER;
  const customValidateEmail = useCallback(
    (value: string) => {
      if (isPartnerForm && notBusinessEmail) {
        return true;
      }
      return validateEmail(value);
    },
    [isPartnerForm, notBusinessEmail]
  );

  const determineShowStateSelect = (countryCode: string) => {
    return (
      (isPartnerForm && countryCode === 'US') || countryCode === 'CA' || countryCode === 'AU' || countryCode === 'NZ'
    );
  };

  const [showStateSelect, setShowStateSelect] = useState(determineShowStateSelect(formikValues['country']));

  useEffect(() => {
    const changedCountry = formikValues['country'];
    setShowStateSelect(determineShowStateSelect(changedCountry));
    formikValues['state'] = showStateSelect ? orderedStateList(changedCountry)[0] : '';
  }, [formikValues['country'], showStateSelect]);

  const trackErrorBlur = useCallback(
    (id: string, validation: (value: string | number) => boolean | string, value: string) => {
      formikRef.current?.setFieldError(id, false);
      formikRef.current?.setFieldTouched(id, true);
      if (validation(value)) {
        formikRef.current?.setFieldError(id, true);
      }
      return false;
    },
    []
  );

  const wrapperClasses = classNames({
    [style.inputGrid]: !isPartnerForm,
    [style.inputGridPartner]: isPartnerForm,
  });

  return (
    <div className={wrapperClasses}>
      {inputs!.map((input: any, index: number) => (
        <FieldRenderer
          key={index}
          input={input}
          index={index}
          fieldValue={formikValues[input.id]}
          fieldError={!!formikErrors[input.id]}
          fieldTouched={!!formikTouched[input.id]}
          stateSelectFormikValues={input.id === 'country' ? formikValues : undefined}
          stateSelectFormikTouched={input.id === 'country' ? formikTouched : undefined}
          stateSelectFormikErrors={input.id === 'country' ? formikErrors : undefined}
          setTextareaExistsSetState={setTextareaExistsSetState}
          trackBlur={trackBlur}
          trackErrorBlur={trackErrorBlur}
          validateBusinessEmail={validateBusinessEmail}
          customValidateEmail={customValidateEmail}
          isPartnerForm={isPartnerForm}
          showStateSelect={showStateSelect}
          notBusinessEmail={notBusinessEmail}
        />
      ))}
    </div>
  );
});

interface FieldRendererProps {
  input: any;
  index: number;
  fieldValue: any;
  fieldError: any;
  fieldTouched: any;
  stateSelectFormikValues?: any;
  stateSelectFormikTouched?: any;
  stateSelectFormikErrors?: any;
  setTextareaExistsSetState: any;
  trackBlur: (id: string, type?: string) => void;
  trackErrorBlur: (id: string, validation: (value: string | number) => boolean | string, value: string) => false;
  validateBusinessEmail: (email: string) => Promise<boolean>;
  customValidateEmail: (value: string) => boolean | string;
  isPartnerForm: boolean;
  showStateSelect: boolean;
  notBusinessEmail: boolean;
}

// FastField already compares Formik state internally.
// Additional prop checks are required here because this field
// depends on external props that FastField does not track.
const shouldEmailFieldUpdate = (next: any, prev: any) =>
  next.name !== prev.name ||
  getIn(next.formik.values, next.name) !== getIn(prev.formik.values, prev.name) ||
  getIn(next.formik.errors, next.name) !== getIn(prev.formik.errors, prev.name) ||
  getIn(next.formik.touched, next.name) !== getIn(prev.formik.touched, prev.name) ||
  next.formik.isSubmitting !== prev.formik.isSubmitting ||
  Object.keys(next).length !== Object.keys(prev).length ||
  next.errormessage !== prev.errormessage ||
  next.validate !== prev.validate ||
  next.required !== prev.required ||
  next.onBlur !== prev.onBlur;

const FieldRenderer = memo(
  ({
    input,
    index,
    fieldValue,
    fieldError,
    fieldTouched,
    stateSelectFormikValues,
    stateSelectFormikTouched,
    stateSelectFormikErrors,
    setTextareaExistsSetState,
    trackBlur,
    trackErrorBlur,
    validateBusinessEmail,
    customValidateEmail,
    isPartnerForm,
    showStateSelect,
    notBusinessEmail,
  }: FieldRendererProps) => {
    const options: any = input.options;
    const type: string = input.type;
    const label: string = input.label;
    const hintMessageText: string = input.placeHolder;
    const id: string = input.id;
    const placeholder: string = input.placeHolder;
    const errorMessageText: string = input.error;
    const required: boolean = input.required;
    const hasError = fieldError && fieldTouched;
    let Component = null;

    switch (type) {
      case 'select':
        Component = (
          <>
            <FastField
              name={id}
              as={Select}
              disabled={false}
              error={hasError}
              valid={fieldValue !== ''}
              errormessage={errorMessageText}
              validate={validateSelect}
              required
              aria-required="true"
              key={index}
              label={label}
              defaultValue={fieldValue}
              onBlur={(event: React.FocusEvent<HTMLSelectElement>) => {
                trackBlur(id);
                trackErrorBlur(id, validateSelect, event.target.value);
              }}
            >
              {id !== 'country' && (
                <option value="" hidden>
                  {placeholder}
                </option>
              )}
              {options!.map((option: any, optionindex: number) => {
                const countryName = Object.keys(option)[0];
                return (
                  <option key={optionindex.toString()} value={option[countryName]}>
                    {countryName}
                  </option>
                );
              })}
            </FastField>
            {id === 'country' && showStateSelect && (
              <ConditionalStateSelect
                formikValues={stateSelectFormikValues}
                formikTouched={stateSelectFormikTouched}
                formikErrors={stateSelectFormikErrors}
                trackBlur={trackBlur}
                trackErrorBlur={trackErrorBlur}
              />
            )}
          </>
        );
        break;
      case 'textarea':
        Component = (
          <FastField
            setTextareaExistsSetState={setTextareaExistsSetState}
            name={type}
            required={false}
            as={TextArea}
            label={label}
            key={index}
            onFocus={() => {
              trackBlur(id, type);
            }}
          />
        );
        break;
      case 'email':
        Component = (
          <FastField
            shouldUpdate={shouldEmailFieldUpdate}
            name={id}
            type={type}
            error={hasError}
            errormessage={
              notBusinessEmail && isPartnerForm ? hintMessageText || 'Please use a business email.' : errorMessageText
            }
            maxLength={128}
            validate={customValidateEmail}
            aria-required="true"
            as={Input}
            label={label}
            key={index}
            required={isPartnerForm}
            onFocus={() => {
              trackBlur(id);
            }}
            onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
              if (!isPartnerForm) {
                validateEmail(event.target.value);
                trackErrorBlur(id, validateEmail, event.target.value);
              } else {
                validateBusinessEmail(event.target.value);
                trackErrorBlur(id, customValidateEmail, event.target.value);
              }
            }}
          />
        );
        break;
      case 'tel':
        Component = (
          <FastField
            name={id}
            type={type}
            error={hasError}
            errormessage={errorMessageText}
            validate={required ? validatePhone : ''}
            aria-required={required ? 'true' : undefined}
            as={Input}
            label={label}
            key={index}
            maxLength="30"
            onFocus={() => {
              trackBlur(id);
            }}
          />
        );
        break;
      case 'hidden':
        Component = <FastField name={id} as={HiddenField} key={index} />;
        break;
      case 'radio': {
        Component = (
          <RadioFieldGroup
            name={id}
            options={options}
            label={label}
            error={hasError}
            errorMessage={errorMessageText}
            onFocus={() => trackBlur(id)}
            onBlur={(event) => trackErrorBlur(id, validateRadio, event.target.checked ? event.target.value : '')}
          />
        );
        break;
      }
      default:
        Component = (
          <FastField
            name={id}
            type={type}
            error={hasError}
            errormessage={errorMessageText}
            validate={id === 'fullName' ? validateFullName : validateString}
            aria-required="true"
            as={Input}
            label={label}
            key={index}
            onFocus={() => {
              trackBlur(id);
            }}
            onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
              trackErrorBlur(id, validateString, event.target.value);
            }}
            index
          />
        );
        break;
    }
    return Component;
  }
);

FieldRenderer.displayName = 'FieldRenderer';
